import React, { memo } from 'react';
import { User, CreditCard, LineChart, Phone, Star } from 'lucide-react';
import { displayDistance } from '../../utils/serviceMetricsUtils';
import { formatMoney } from '../../utils/formatMoney';
import localeConfig from '../../config/locale.config';

const formatTimeOnly = (date) => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString(localeConfig.intlLocale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return '—';
  }
};

const SectionIcon = ({ children }) => (
  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">{children}</div>
);

const clampRating = (n) => Math.max(0, Math.min(5, Number(n) || 0));

const FeedbackStarRow = ({ label, rating }) => {
  const r = clampRating(rating);
  const hasRating = r > 0;
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex justify-center gap-0.5 sm:justify-end">
          {Array.from({ length: 5 }).map((_, idx) => (
            <Star
              key={idx}
              className={`h-5 w-5 ${idx < r ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
              strokeWidth={1.5}
              aria-hidden
            />
          ))}
        </div>
        {hasRating ? (
          <span className="text-xs font-medium tabular-nums text-slate-600">{r.toFixed(1)} / 5</span>
        ) : (
          <span className="text-xs text-slate-400">Not rated</span>
        )}
      </div>
    </div>
  );
};

/**
 * Driver, Payment (Stripe-style), and Cost summary blocks below Vendor on the workspace rail.
 * Accepts payment from parent to avoid duplicate API calls.
 */
const ServiceWorkspaceLowerRails = ({ detailedServiceData, payment, paymentLoading }) => {
  const svc = detailedServiceData?.service;
  const driver = detailedServiceData?.driver;

  const driverName = driver?.name || driver?.driver_user?.name || '—';
  const driverPhone =
    driver?.mobile_number || driver?.mobile || driver?.driver_user?.mobile_number || '';
  const driverInitial = (driverName && driverName.charAt(0).toUpperCase()) || '?';

  const telDigits = driverPhone ? String(driverPhone).replace(/[^\d+]/g, '') : '';
  const telLink = telDigits ? `tel:${telDigits}` : null;

  const providerLabel = (payment?.provider && String(payment.provider).toUpperCase()) || 'STRIPE';

  let statusTone = 'bg-slate-100 text-slate-800 ring-slate-600/10';
  const st = payment?.status;
  if (st === 'succeeded') statusTone = 'bg-emerald-50 text-emerald-900 ring-emerald-600/20';
  else if (st === 'pending') statusTone = 'bg-amber-50 text-amber-900 ring-amber-600/20';
  else if (st === 'failed') statusTone = 'bg-red-50 text-red-800 ring-red-600/15';

  const statusReadable = st
    ? String(st)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : '—';

  const fb = detailedServiceData?.feedback;
  const feedbackSubmitted = Boolean(detailedServiceData?.feedback_submitted && fb);
  const feedbackText = (fb?.feedback_text || '').trim();

  const estimated = formatMoney(svc?.estimated_cost, svc?.currency);
  const finalAmount =
    payment?.amount != null && !Number.isNaN(Number(payment.amount))
      ? formatMoney(payment.amount, payment.currency)
      : '—';

  return (
    <div className="flex flex-col gap-4">
      {/* Driver */}
      <div className="cwp-surface-card-elevated relative overflow-hidden">
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <SectionIcon>
            <User className="h-4 w-4" strokeWidth={2} aria-hidden />
          </SectionIcon>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Driver</h3>
            <p className="text-xs text-slate-500">On-ground operator</p>
          </div>
        </div>

        <div className="px-5 py-4">
        {driver ? (
          <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-100">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white shadow-sm">
              {driverInitial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900">{driverName}</div>
              <div className="mt-0.5 text-sm text-slate-600">{driverPhone || '—'}</div>
            </div>
            {telLink ? (
              <a
                href={telLink}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition-colors hover:bg-slate-50 hover:text-blue-600"
                aria-label={`Call ${driverName}`}
                title="Call driver"
              >
                <Phone className="h-4 w-4" aria-hidden />
              </a>
            ) : null}
          </div>
        ) : (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
            No driver assigned for this service yet.
          </p>
        )}
        </div>
      </div>

      {/* Payment */}
      <div className="cwp-surface-card-elevated relative overflow-hidden">
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <SectionIcon>
            <CreditCard className="h-4 w-4" strokeWidth={2} aria-hidden />
          </SectionIcon>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Payment</h3>
            <p className="text-xs text-slate-500">
              {payment?.provider ? `${payment.provider} charge` : 'Customer payment'}
            </p>
          </div>
        </div>

        <div className="px-5 py-4">
        {paymentLoading ? (
          <p className="text-sm text-slate-500">Loading payment…</p>
        ) : !payment ? (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
            No payment recorded for this service yet.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-100">
              <span className="rounded-md bg-violet-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                {providerLabel}
              </span>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${statusTone}`}
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-80" aria-hidden />
                {statusReadable}
              </span>
              <span className="ml-auto text-lg font-bold tracking-tight text-slate-900">
                {formatMoney(payment.amount, payment.currency)}
              </span>
            </div>
            <div className="rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-100">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Payment ID</div>
              <div className="mt-1 break-all font-mono text-xs text-slate-800">{payment.transaction_reference || payment.payment_intent_id || payment.provider_payment_id || '—'}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-100">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Created</div>
                <div className="mt-1 text-sm font-medium text-slate-900">{formatTimeOnly(payment.created_at)}</div>
              </div>
              <div className="rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-100">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Captured</div>
                <div className="mt-1 text-sm font-medium text-slate-900">{formatTimeOnly(payment.succeeded_at)}</div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Cost summary */}
      <div className="cwp-surface-card-elevated relative overflow-hidden">
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <SectionIcon>
            <LineChart className="h-4 w-4" strokeWidth={2} aria-hidden />
          </SectionIcon>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Cost summary</h3>
          </div>
        </div>

        <div className="space-y-3 px-5 py-4">
        <div className="space-y-3 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-100">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Distance</span>
            <span className="font-medium text-slate-900">{displayDistance(svc)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Estimated</span>
            <span className="font-medium text-slate-900">{estimated}</span>
          </div>
          <div className="flex items-center justify-between border-t border-slate-200/80 pt-3 text-sm">
            <span className="font-bold text-slate-900">Final</span>
            <span className="font-bold text-slate-900">{finalAmount}</span>
          </div>
        </div>
        </div>
      </div>

      {/* Customer feedback */}
      <div className="cwp-surface-card-elevated relative overflow-hidden">
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <SectionIcon>
            <Star className="h-4 w-4" strokeWidth={2} aria-hidden />
          </SectionIcon>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Customer feedback</h3>
            <p className="text-xs text-slate-500">Post-service survey</p>
          </div>
        </div>
        <div className="px-5 py-4">
        <div className="rounded-xl bg-slate-50 px-4 py-5 text-left ring-1 ring-slate-100">
          {feedbackSubmitted ? (
            <div className="space-y-4">
              <FeedbackStarRow label="Overall experience" rating={fb?.overall_rating} />
              <FeedbackStarRow label="Driver behaviour" rating={fb?.driver_rating} />
              <FeedbackStarRow label="Service quality" rating={fb?.service_quality_rating} />
              <FeedbackStarRow label="Response time" rating={fb?.response_time_rating} />
              <div className="border-t border-slate-200/80 pt-4">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Written feedback</div>
                <p className="mt-2 text-sm leading-relaxed text-slate-800">
                  {feedbackText || <span className="text-slate-400">No written comments.</span>}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-center gap-0.5 opacity-40">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <Star key={idx} className="h-6 w-6 text-slate-400" strokeWidth={1.5} aria-hidden />
                ))}
              </div>
              <p className="mt-3 text-sm text-slate-600">No rating yet</p>
              <p className="text-xs text-slate-500">Survey not completed.</p>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ServiceWorkspaceLowerRails);
